// Mappings of one CRM connection (admin): which CRM object feeds which call-center list and how its fields map,
// what each disposition does in the CRM, and which CRM user each portal user is (owner of calls and tasks).
// Field names are picked from the CRM's own metadata (read live through the API), so a typo cannot be saved.
import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus, Trash2, Pencil, RefreshCw, CheckCircle2, Wand2 } from 'lucide-react';
import { DataTable, ErrorBox, Field } from '@/components/common';
import { Button } from '@/components/ui/button';
import { Input, Select, Checkbox } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { StatusBadge, Badge } from '@/components/ui/badge';
import { toast } from '@/store/toast';
import { useCan } from '@/store/auth';
import { useRefOptions } from '@/hooks/meta';
import { enumLabel } from '@/lib/utils';
import { t } from '@/i18n';
import { crmApi, crmErrorText, type Connection, type CrmUser, type DispositionMapping, type ObjectMapping, type TaskConfig, type UserMapping } from './types';

const TRANSFORMS = ['NONE', 'TRIM', 'UPPER', 'LOWER', 'PHONE_E164', 'EMAIL', 'DATE', 'DATETIME_UTC', 'BOOLEAN'];
const LOCAL_FIELDS = ['phone_number', 'first_name', 'last_name', 'email', 'timezone'];
const DIRECTIONS = ['CRM_TO_CC', 'CC_TO_CRM', 'BIDIRECTIONAL'];

interface ObjMeta { apiName: string; label: string; queryable: boolean }
interface FieldMeta { apiName: string; label: string; type: string; readOnly: boolean }

export function MappingsTab({ c }: { c: Connection }) {
  return (
    <div className="grid gap-4">
      <ObjectMappings c={c} />
      <DispositionMappings c={c} />
      <UserMappings c={c} />
    </div>
  );
}

function useObjects(c: Connection, enabled: boolean) {
  return useQuery({ queryKey: ['crm-objects', c.id], enabled: enabled && c.authorized, staleTime: 600_000,
                    queryFn: () => crmApi<{ objects: ObjMeta[] }>(`/connections/${c.id}/metadata/objects`) });
}
function useFields(c: Connection, object: string) {
  return useQuery({ queryKey: ['crm-fields', c.id, object], enabled: !!object && c.authorized, staleTime: 600_000,
                    queryFn: () => crmApi<{ fields: FieldMeta[] }>(`/connections/${c.id}/metadata/objects/${object}/fields`) });
}

function ObjectMappings({ c }: { c: Connection }) {
  const q = useQuery({ queryKey: ['crm-object-mappings', c.id], queryFn: () => crmApi<{ mappings: ObjectMapping[] }>(`/connections/${c.id}/object-mappings`) });
  const can = useCan('crm.mappings'), canSync = useCan('crm.sync');
  const lists = useRefOptions('contact-lists');
  const [editing, setEditing] = React.useState<ObjectMapping | 'new' | null>(null);
  const [fieldsOf, setFieldsOf] = React.useState<ObjectMapping | null>(null);
  async function sync(m: ObjectMapping, type: 'FULL' | 'INCREMENTAL') {
    try { await crmApi(`/connections/${c.id}/sync`, { body: { mapping_id: m.id, type } }); toast.ok(t('Sync started')); } catch (e) { toast.error(crmErrorText(e)); }
  }
  async function remove(m: ObjectMapping) {
    if (!confirm(t('Remove the mapping of {{object}}?', { object: m.remote_object }))) return;
    try { await crmApi(`/connections/${c.id}/object-mappings/${m.id}`, { method: 'DELETE' }); q.refetch(); } catch (e) { toast.error(crmErrorText(e)); }
  }
  const listName = (id?: number | null) => (lists.data ?? []).find((l) => l.id === id)?.name;
  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-3">
        <div><CardTitle>{t('Objects')}</CardTitle><CardDescription>{t('CRM records synced into a contact list for the dialer, and used for screen pop.')}</CardDescription></div>
        {can && <Button variant="outline" onClick={() => setEditing('new')} disabled={!c.authorized}><Plus /> {t('Add object')}</Button>}
      </CardHeader>
      <CardContent>
        <ErrorBox error={q.error} />
        <DataTable rowKey={(r) => r.id} rows={q.data?.mappings} empty={c.authorized ? t('No objects mapped.') : t('Authorize the connection to map objects.')} columns={[
          { key: 'remote_object', label: t('CRM object'), render: (r) => <span className="font-medium">{r.remote_object}</span> },
          { key: 'local_entity', label: t('As'), render: (r) => enumLabel(r.local_entity) },
          { key: 'sync_direction', label: t('Direction'), render: (r) => enumLabel(r.sync_direction) },
          { key: 'conflict_policy', label: t('On conflict'), render: (r) => enumLabel(r.conflict_policy) },
          { key: 'list', label: t('Contact list'), render: (r) => listName(r.configuration_json?.list_id) ?? '-' },
          { key: 'fields', label: t('Fields'), render: (r) => r.fields.length },
          { key: 'enabled', label: t('Status'), render: (r) => <StatusBadge value={r.enabled ? 'ACTIVE' : 'DISABLED'} /> }]}
          actions={(r) => <>
            <Button size="sm" variant="outline" onClick={() => setFieldsOf(r)}>{t('Fields')}</Button>
            {canSync && r.sync_direction !== 'NONE' && <Button size="icon" variant="ghost" aria-label={t('Sync {{object}}', { object: r.remote_object })} title={t('Sync changes now')} onClick={() => sync(r, 'INCREMENTAL')}><RefreshCw /></Button>}
            {can && <Button size="icon" variant="ghost" aria-label={t('Edit {{name}}', { name: r.remote_object })} onClick={() => setEditing(r)}><Pencil /></Button>}
            {can && <Button size="icon" variant="ghost" aria-label={t('Remove {{name}}', { name: r.remote_object })} onClick={() => remove(r)}><Trash2 className="text-destructive" /></Button>}
          </>} />
      </CardContent>
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        {editing && <DialogContent title={editing === 'new' ? t('Add object') : t('Edit {{name}}', { name: editing.remote_object })} className="max-w-lg">
          <ObjectForm c={c} m={editing === 'new' ? null : editing} onDone={() => { setEditing(null); q.refetch(); }} />
        </DialogContent>}
      </Dialog>
      <Dialog open={!!fieldsOf} onOpenChange={(o) => !o && setFieldsOf(null)}>
        {fieldsOf && <DialogContent title={t('Fields of {{object}}', { object: fieldsOf.remote_object })} className="max-w-3xl">
          <FieldMappings c={c} m={q.data?.mappings.find((x) => x.id === fieldsOf.id) ?? fieldsOf} onChange={() => q.refetch()} />
        </DialogContent>}
      </Dialog>
    </Card>
  );
}

function ObjectForm({ c, m, onDone }: { c: Connection; m: ObjectMapping | null; onDone: () => void }) {
  const objects = useObjects(c, true);
  const lists = useRefOptions('contact-lists');
  const [v, setV] = React.useState({ remote_object: m?.remote_object ?? '', local_entity: m?.local_entity ?? 'CONTACT', sync_direction: m?.sync_direction ?? 'CRM_TO_CC',
    conflict_policy: m?.conflict_policy ?? 'CRM_WINS', use_for_lookup: m ? !!m.use_for_lookup : true, enabled: m ? !!m.enabled : true,
    list_id: m?.configuration_json?.list_id ? String(m.configuration_json.list_id) : '' });
  const [error, setError] = React.useState<unknown>(null);
  const set = (k: keyof typeof v) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setV((x) => ({ ...x, [k]: e.target.value }));
  async function save(e: React.FormEvent) {
    e.preventDefault(); setError(null);
    const body = { remote_object: v.remote_object, local_entity: v.local_entity, sync_direction: v.sync_direction, conflict_policy: v.conflict_policy,
                   use_for_lookup: v.use_for_lookup, enabled: v.enabled, configuration: { list_id: v.list_id ? Number(v.list_id) : null } };
    try {
      if (m) await crmApi(`/connections/${c.id}/object-mappings/${m.id}`, { method: 'PATCH', body });
      else await crmApi(`/connections/${c.id}/object-mappings`, { body });
      toast.ok(t('Saved')); onDone();
    } catch (err) { setError(new Error(crmErrorText(err))); }
  }
  return (
    <form onSubmit={save} className="grid gap-3">
      <Field help="crmobject.remote_object" label={t('CRM object')}><Select value={v.remote_object} onChange={set('remote_object')} required disabled={!!m}>
        <option value="">{objects.isLoading ? t('Loading…') : t('Choose…')}</option>
        {(objects.data?.objects ?? []).filter((o) => o.queryable).map((o) => <option key={o.apiName} value={o.apiName}>{o.label} ({o.apiName})</option>)}
        {m && <option value={m.remote_object}>{m.remote_object}</option>}
      </Select></Field>
      <Field help="crmobject.local_entity" label={t('Used as')}><Select value={v.local_entity} onChange={set('local_entity')}>
        {['CONTACT', 'LEAD', 'ACCOUNT', 'CALL_ACTIVITY', 'FOLLOW_UP_TASK'].map((x) => <option key={x} value={x}>{enumLabel(x)}</option>)}
      </Select></Field>
      <Field help="crmobject.sync_direction" label={t('Direction')}><Select value={v.sync_direction} onChange={set('sync_direction')}>
        {['NONE', ...DIRECTIONS].map((x) => <option key={x} value={x}>{enumLabel(x)}</option>)}
      </Select></Field>
      <Field help="crmobject.conflict_policy" label={t('On conflict')} hint={t('When a record changed on both sides since the last sync.')}><Select value={v.conflict_policy} onChange={set('conflict_policy')}>
        {['CRM_WINS', 'CC_WINS', 'LATEST_WINS', 'MANUAL'].map((x) => <option key={x} value={x}>{enumLabel(x)}</option>)}
      </Select></Field>
      <Field help="crmobject.list_id" label={t('Contact list')} hint={t('Synced records land in this list.')}><Select value={v.list_id} onChange={set('list_id')}>
        <option value="">{t('None')}</option>{(lists.data ?? []).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
      </Select></Field>
      <label className="flex items-center gap-2 text-sm"><Checkbox checked={v.use_for_lookup} onChange={(e) => setV((x) => ({ ...x, use_for_lookup: e.target.checked }))} /> {t('Search it for screen pop')}</label>
      <label className="flex items-center gap-2 text-sm"><Checkbox checked={v.enabled} onChange={(e) => setV((x) => ({ ...x, enabled: e.target.checked }))} /> {t('Enabled')}</label>
      <ErrorBox error={error} />
      <div className="flex justify-end"><Button type="submit">{t('Save')}</Button></div>
    </form>
  );
}

function FieldMappings({ c, m, onChange }: { c: Connection; m: ObjectMapping; onChange: () => void }) {
  const can = useCan('crm.mappings');
  const fields = useFields(c, m.remote_object);
  const [v, setV] = React.useState({ local_field: 'phone_number', remote_field: '', transformation: 'NONE', direction: '', is_match_key: false });
  const [error, setError] = React.useState<unknown>(null);
  async function add(e: React.FormEvent) {
    e.preventDefault(); setError(null);
    try {
      await crmApi(`/connections/${c.id}/object-mappings/${m.id}/fields`, { body: { local_field: v.local_field, remote_field: v.remote_field, transformation: v.transformation,
        direction: v.direction || null, is_match_key: v.is_match_key } });
      setV((x) => ({ ...x, remote_field: '', is_match_key: false })); onChange();
    } catch (err) { setError(new Error(crmErrorText(err))); }
  }
  async function remove(id: number) {
    try { await crmApi(`/connections/${c.id}/object-mappings/${m.id}/fields/${id}`, { method: 'DELETE' }); onChange(); } catch (err) { toast.error(crmErrorText(err)); }
  }
  async function check() {
    try {
      const r = await crmApi<{ unknown: string[]; readOnly: string[] }>(`/connections/${c.id}/object-mappings/${m.id}/check`, { method: 'POST', body: {} });
      if (!r.unknown.length && !r.readOnly.length) toast.ok(t('Every mapped field exists in the CRM.'));
      else toast.error(t('Check these fields: {{list}}', { list: [...r.unknown, ...r.readOnly].join(', ') }));
    } catch (err) { toast.error(crmErrorText(err)); }
  }
  return (
    <div className="grid gap-3">
      <DataTable rowKey={(r) => r.id} rows={m.fields} empty={t('No fields mapped.')} columns={[
        { key: 'local_field', label: t('Call center') }, { key: 'remote_field', label: t('CRM field') },
        { key: 'transformation', label: t('Transform'), render: (r) => enumLabel(r.transformation) },
        { key: 'direction', label: t('Direction'), render: (r) => (r.direction ? enumLabel(r.direction) : t('as the object')) },
        { key: 'is_match_key', label: t('Match key'), render: (r) => (r.is_match_key ? <Badge variant="info">{t('yes')}</Badge> : '') }]}
        actions={(r) => can ? <Button size="icon" variant="ghost" aria-label={t('Remove {{name}}', { name: r.remote_field })} onClick={() => remove(r.id)}><Trash2 className="text-destructive" /></Button> : null} />
      {can && <form onSubmit={add} className="grid items-end gap-2 md:grid-cols-6">
        <Field help="crmfield.local_field" label={t('Call center')}><Select value={v.local_field} onChange={(e) => setV((x) => ({ ...x, local_field: e.target.value }))}>
          {LOCAL_FIELDS.map((f) => <option key={f} value={f}>{f}</option>)}
        </Select></Field>
        <Field help="crmfield.remote_field" label={t('CRM field')}><Select value={v.remote_field} onChange={(e) => setV((x) => ({ ...x, remote_field: e.target.value }))} required>
          <option value="">{fields.isLoading ? t('Loading…') : t('Choose…')}</option>
          {(fields.data?.fields ?? []).map((f) => <option key={f.apiName} value={f.apiName}>{f.label} ({f.apiName})</option>)}
        </Select></Field>
        <Field help="crmfield.transformation" label={t('Transform')}><Select value={v.transformation} onChange={(e) => setV((x) => ({ ...x, transformation: e.target.value }))}>
          {TRANSFORMS.map((x) => <option key={x} value={x}>{enumLabel(x)}</option>)}
        </Select></Field>
        <Field help="crmfield.direction" label={t('Direction')}><Select value={v.direction} onChange={(e) => setV((x) => ({ ...x, direction: e.target.value }))}>
          <option value="">{t('as the object')}</option>{DIRECTIONS.map((x) => <option key={x} value={x}>{enumLabel(x)}</option>)}
        </Select></Field>
        <label className="flex h-9 items-center gap-2 text-sm"><Checkbox checked={v.is_match_key} onChange={(e) => setV((x) => ({ ...x, is_match_key: e.target.checked }))} /> {t('Match key')}</label>
        <Button type="submit" variant="outline"><Plus /> {t('Add')}</Button>
      </form>}
      <ErrorBox error={error} />
      <div className="flex justify-end"><Button variant="outline" size="sm" onClick={check}><CheckCircle2 /> {t('Check against the CRM')}</Button></div>
    </div>
  );
}

function DispositionMappings({ c }: { c: Connection }) {
  const q = useQuery({ queryKey: ['crm-dispo-mappings', c.id], queryFn: () => crmApi<{ mappings: DispositionMapping[] }>(`/connections/${c.id}/disposition-mappings`) });
  const can = useCan('crm.mappings');
  const campaigns = useRefOptions('campaigns');
  const [editing, setEditing] = React.useState<DispositionMapping | 'new' | null>(null);
  async function remove(m: DispositionMapping) {
    try { await crmApi(`/connections/${c.id}/disposition-mappings/${m.id}`, { method: 'DELETE' }); q.refetch(); } catch (e) { toast.error(crmErrorText(e)); }
  }
  const camp = (id: number | null) => (id ? (campaigns.data ?? []).find((x) => x.id === id)?.name ?? `#${id}` : t('All campaigns'));
  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-3">
        <div><CardTitle>{t('Dispositions')}</CardTitle><CardDescription>{t('What an agent\'s disposition does in the CRM besides logging the call.')}</CardDescription></div>
        {can && <Button variant="outline" onClick={() => setEditing('new')}><Plus /> {t('Add rule')}</Button>}
      </CardHeader>
      <CardContent>
        <DataTable rowKey={(r) => r.id} rows={q.data?.mappings} empty={t('No rules: dispositions are only written on the call activity.')} columns={[
          { key: 'disposition_code', label: t('Disposition'), render: (r) => <span className="font-medium">{r.disposition_code}</span> },
          { key: 'campaign_id', label: t('Campaign'), render: (r) => camp(r.campaign_id) },
          { key: 'action', label: t('Action'), render: (r) => enumLabel(r.action) },
          { key: 'detail', label: t('Detail'), render: (r) => [r.remote_field && `${r.remote_field} = ${r.remote_value ?? ''}`, r.task_config_json && t('task "{{subject}}"', { subject: r.task_config_json.subject })].filter(Boolean).join(' · ') || '-' },
          { key: 'enabled', label: t('Status'), render: (r) => <StatusBadge value={r.enabled ? 'ACTIVE' : 'DISABLED'} /> }]}
          actions={(r) => can ? <>
            <Button size="icon" variant="ghost" aria-label={t('Edit {{name}}', { name: r.disposition_code })} onClick={() => setEditing(r)}><Pencil /></Button>
            <Button size="icon" variant="ghost" aria-label={t('Remove {{name}}', { name: r.disposition_code })} onClick={() => remove(r)}><Trash2 className="text-destructive" /></Button>
          </> : null} />
      </CardContent>
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        {editing && <DialogContent title={editing === 'new' ? t('Add rule') : t('Edit {{name}}', { name: editing.disposition_code })} className="max-w-lg">
          <DispositionForm c={c} m={editing === 'new' ? null : editing} onDone={() => { setEditing(null); q.refetch(); }} />
        </DialogContent>}
      </Dialog>
    </Card>
  );
}

function DispositionForm({ c, m, onDone }: { c: Connection; m: DispositionMapping | null; onDone: () => void }) {
  const dispositions = useRefOptions('dispositions');
  const campaigns = useRefOptions('campaigns');
  const task: TaskConfig = m?.task_config_json ?? { subject: '', due_from: 'CALL_END', due_offset_min: 1440, assign_to: 'AGENT', priority: 'NORMAL' };
  const [v, setV] = React.useState({ disposition_code: m?.disposition_code ?? '', campaign_id: m?.campaign_id ? String(m.campaign_id) : '', action: m?.action ?? 'CREATE_TASK',
    remote_object: m?.remote_object ?? '', remote_field: m?.remote_field ?? '', remote_value: m?.remote_value ?? '', activity_result_value: m?.activity_result_value ?? '',
    enabled: m ? !!m.enabled : true, ...task, due_offset_min: String(task.due_offset_min ?? 0) });
  const [error, setError] = React.useState<unknown>(null);
  const set = (k: keyof typeof v) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setV((x) => ({ ...x, [k]: e.target.value }));
  const withField = v.action === 'SET_FIELD' || v.action === 'SET_FIELD_AND_TASK';
  const withTask = v.action === 'CREATE_TASK' || v.action === 'SET_FIELD_AND_TASK';
  const codes = [...new Set((dispositions.data ?? []).map((d) => String(d.code)))];
  async function save(e: React.FormEvent) {
    e.preventDefault(); setError(null);
    const body = {
      disposition_code: v.disposition_code, campaign_id: v.campaign_id ? Number(v.campaign_id) : null, action: v.action, enabled: v.enabled,
      activity_result_value: v.activity_result_value || null,
      remote_object: withField ? v.remote_object || null : null, remote_field: withField ? v.remote_field || null : null, remote_value: withField ? v.remote_value || null : null,
      task_config: withTask ? { subject: v.subject, due_from: v.due_from, due_offset_min: Number(v.due_offset_min) || 0, assign_to: v.assign_to, priority: v.priority } : null,
    };
    try {
      if (m) await crmApi(`/connections/${c.id}/disposition-mappings/${m.id}`, { method: 'PATCH', body });
      else await crmApi(`/connections/${c.id}/disposition-mappings`, { body });
      toast.ok(t('Saved')); onDone();
    } catch (err) { setError(new Error(crmErrorText(err))); }
  }
  return (
    <form onSubmit={save} className="grid gap-3">
      <div className="grid grid-cols-2 gap-3">
        <Field help="crmdispo.disposition_code" label={t('Disposition')}><Select value={v.disposition_code} onChange={set('disposition_code')} required>
          <option value="">{t('Choose…')}</option>{codes.map((x) => <option key={x} value={x}>{x}</option>)}
        </Select></Field>
        <Field help="crmdispo.campaign" label={t('Campaign')}><Select value={v.campaign_id} onChange={set('campaign_id')}>
          <option value="">{t('All campaigns')}</option>{(campaigns.data ?? []).map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
        </Select></Field>
      </div>
      <Field help="crmdispo.action" label={t('Action')}><Select value={v.action} onChange={set('action')}>
        {['NONE', 'SET_FIELD', 'CREATE_TASK', 'SET_FIELD_AND_TASK'].map((x) => <option key={x} value={x}>{enumLabel(x)}</option>)}
      </Select></Field>
      <Field help="crmdispo.result_value" label={t('Call result value')} hint={t('Written in the call activity\'s result field, if the CRM has one. Empty: the disposition name.')}>
        <Input value={v.activity_result_value} onChange={set('activity_result_value')} maxLength={255} /></Field>
      {withField && <div className="grid grid-cols-3 gap-3">
        <Field help="crmdispo.remote_object" label={t('CRM object')}><Input value={v.remote_object} onChange={set('remote_object')} placeholder={c.provider === 'ZOHO' ? 'Leads' : 'Lead'} /></Field>
        <Field help="crmdispo.remote_field" label={t('CRM field')}><Input value={v.remote_field} onChange={set('remote_field')} placeholder={c.provider === 'ZOHO' ? 'Lead_Status' : 'Status'} required /></Field>
        <Field help="crmdispo.remote_value" label={t('Value')}><Input value={v.remote_value} onChange={set('remote_value')} /></Field>
      </div>}
      {withTask && <div className="grid grid-cols-2 gap-3">
        <Field help="crmdispo.task_subject" label={t('Task subject')} hint={t('{contact_name} and {disposition} are filled in.')}><Input value={v.subject} onChange={set('subject')} required maxLength={255} /></Field>
        <Field help="crmdispo.task_priority" label={t('Priority')}><Select value={v.priority} onChange={set('priority')}>{['LOW', 'NORMAL', 'HIGH'].map((x) => <option key={x} value={x}>{enumLabel(x)}</option>)}</Select></Field>
        <Field help="crmdispo.due_from" label={t('Due')}><Select value={v.due_from} onChange={set('due_from')}>
          <option value="CALL_END">{t('After the call ends')}</option><option value="CALLBACK_TIME">{t('At the callback time')}</option>
        </Select></Field>
        <Field help="crmdispo.due_offset_min" label={t('Plus minutes')}><Input type="number" min={0} value={v.due_offset_min} onChange={set('due_offset_min')} /></Field>
        <Field help="crmdispo.assign_to" label={t('Assign to')}><Select value={v.assign_to} onChange={set('assign_to')}>
          <option value="AGENT">{t('The agent')}</option><option value="RECORD_OWNER">{t('The record owner')}</option><option value="NONE">{t('Nobody')}</option>
        </Select></Field>
      </div>}
      <label className="flex items-center gap-2 text-sm"><Checkbox checked={v.enabled} onChange={(e) => setV((x) => ({ ...x, enabled: e.target.checked }))} /> {t('Enabled')}</label>
      <ErrorBox error={error} />
      <div className="flex justify-end"><Button type="submit">{t('Save')}</Button></div>
    </form>
  );
}

function UserMappings({ c }: { c: Connection }) {
  const q = useQuery({ queryKey: ['crm-user-mappings', c.id], queryFn: () => crmApi<{ mappings: UserMapping[] }>(`/connections/${c.id}/user-mappings`) });
  const users = useRefOptions('users');
  const can = useCan('crm.mappings');
  const crmUsers = useQuery({ queryKey: ['crm-users', c.id], enabled: can && c.authorized, staleTime: 300_000,
                              queryFn: () => crmApi<{ users: CrmUser[] }>(`/connections/${c.id}/crm-users`) });
  async function auto() {
    try {
      const r = await crmApi<{ matched: number }>(`/connections/${c.id}/user-mappings/auto`, { method: 'POST', body: {} });
      toast.ok(t('{{n}} users matched by e-mail', { n: r.matched ?? 0 })); q.refetch();
    } catch (e) { toast.error(crmErrorText(e)); }
  }
  async function setUser(userId: number, remoteId: string) {
    const u = (crmUsers.data?.users ?? []).find((x) => x.id === remoteId);
    try {
      await crmApi(`/connections/${c.id}/user-mappings/${userId}`, { method: 'PUT', body: { remote_user_id: remoteId || null, remote_user_name: u?.name ?? null, remote_email: u?.email ?? null } });
      q.refetch();
    } catch (e) { toast.error(crmErrorText(e)); }
  }
  const mapped = new Map((q.data?.mappings ?? []).map((m) => [m.user_id, m]));
  const rows = (users.data ?? []).filter((u) => u.role !== 'PLATFORM').map((u) => ({ id: Number(u.id), username: String(u.username), name: String(u.name ?? ''), m: mapped.get(Number(u.id)) }));
  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-3">
        <div><CardTitle>{t('Users')}</CardTitle><CardDescription>{t('The CRM user each portal user is: owner of the calls, tasks and records they create.')}</CardDescription></div>
        {can && <Button variant="outline" onClick={auto} disabled={!c.authorized}><Wand2 /> {t('Match by e-mail')}</Button>}
      </CardHeader>
      <CardContent>
        <ErrorBox error={crmUsers.error} />
        <DataTable rowKey={(r) => r.id} rows={rows} empty={t('No users.')} columns={[
          { key: 'username', label: t('User'), render: (r) => <span>{r.name} <span className="text-muted-foreground">({r.username})</span></span> },
          { key: 'crm', label: t('CRM user'), render: (r) => can && crmUsers.data
            ? <Select className="h-8 max-w-xs" value={r.m?.remote_user_id ?? ''} onChange={(e) => setUser(r.id, e.target.value)} aria-label={t('CRM user of {{name}}', { name: r.username })}>
                <option value="">{t('Nobody')}</option>
                {crmUsers.data.users.filter((u) => u.active || u.id === r.m?.remote_user_id).map((u) => <option key={u.id} value={u.id}>{u.name}{u.email ? ` <${u.email}>` : ''}</option>)}
              </Select>
            : (r.m?.remote_user_name ?? r.m?.remote_user_id ?? '-') },
          { key: 'matched_by', label: t('Matched by'), render: (r) => (r.m ? enumLabel(r.m.matched_by) : '') }]} />
      </CardContent>
    </Card>
  );
}
